<template>
  <div class="tag-picker shadow">
    <div class="d-flex align-items-center mb-2" style="gap: 8px">
      <select v-model="type" aria-label="Record type" class="form-control form-control-sm" style="max-width: 190px" @change="search">
        <option value="">All record types</option>
        <option v-for="t in types" :key="t.key" :value="t.key">{{ t.label }}</option>
      </select>
      <input
        ref="input"
        v-model="q"
        aria-label="Search by name, number or code"
        class="form-control form-control-sm"
        placeholder="Search by name, number or code…"
        @input="debouncedSearch"
        @keydown.esc.prevent="$emit('close')"
        @keydown.enter.prevent="pickFirst"
      />
    </div>

    <div class="tag-picker-results">
      <p v-if="!types.length" class="text-muted small mb-0 px-1">You can't tag records.</p>
      <p v-else-if="q.trim().length < 2 && !/^\d+$/.test(q.trim())" class="text-muted small mb-0 px-1">
        Type at least 2 characters to find a record to tag.
      </p>
      <p v-else-if="loading" class="text-muted small mb-0 px-1">Searching…</p>
      <p v-else-if="!groups.length" class="text-muted small mb-0 px-1">No matching records you can tag.</p>
      <div v-for="g in groups" :key="g.type" class="mb-1">
        <div class="tag-picker-group">{{ g.label }}</div>
        <button
          v-for="r in g.results"
          :key="g.type + r.id"
          type="button"
          class="tag-picker-item"
          @click="pick(r, g.label)"
        >
          <span class="font-weight-bold">{{ r.label }}</span>
          <small v-if="r.sublabel" class="text-muted ml-2">{{ r.sublabel }}</small>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import api from "../api";

// Search records the user may tag (limited by the API to their agency and role).
export default {
  name: "EntityTagPicker",
  props: {
    types: { type: Array, default: () => [] }, // [{key, label}]
  },
  data() {
    return { type: "", q: "", groups: [], loading: false, timer: null };
  },
  mounted() {
    this.$nextTick(() => this.$refs.input && this.$refs.input.focus());
  },
  methods: {
    debouncedSearch() {
      clearTimeout(this.timer);
      this.timer = setTimeout(this.search, 300);
    },
    search() {
      const q = this.q.trim();
      if (q.length < 2 && !/^\d+$/.test(q)) {
        this.groups = [];
        return;
      }
      this.loading = true;
      const type = this.type;
      api
        .searchEntities(q, type)
        .then((data) => {
          if (q !== this.q.trim() || type !== this.type) return; // a newer search is running
          if (type) {
            const t = this.types.find((x) => x.key === type);
            this.groups = data.length ? [{ type, label: t ? t.label : type, results: data }] : [];
          } else {
            const allowed = new Set(this.types.map((t) => t.key));
            this.groups = data.filter((g) => allowed.has(g.type));
          }
        })
        .catch(() => {
          this.groups = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    pick(result, typeLabel) {
      this.$emit("pick", { typeLabel, ...result });
    },
    pickFirst() {
      const g = this.groups[0];
      if (g && g.results.length) this.pick(g.results[0], g.label);
    },
  },
};
</script>

<style scoped>
.tag-picker {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px;
}
.tag-picker-results {
  max-height: 240px;
  overflow-y: auto;
}
.tag-picker-group {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
  padding: 6px 6px 2px;
}
.tag-picker-item {
  display: block;
  width: 100%;
  text-align: left;
  border: 0;
  background: transparent;
  padding: 6px 8px;
  border-radius: 6px;
  font-size: 0.85rem;
}
.tag-picker-item:hover,
.tag-picker-item:focus {
  background: #eef2ff;
  outline: none;
}
</style>
